/**
 * Rule 46 Ship 3 — invoice-template overrides.
 *
 * The base CSS in `printInvoice.js` / `printDocuments.js` is styled for the
 * default "modern" look (blue accents, boxed sections). Each named template
 * here appends a small stylesheet that overrides just the differentiating
 * properties — colors, borders, spacing — while leaving compliance-critical
 * content (Rule 46 fields, HSN summary, sign-block) intact.
 *
 * Consumed by:
 *   • printInvoice.js  → Tax Invoice (default variant)
 *   • printDocuments.js → Bill of Supply / Credit / Debit / Receipt / Refund
 *                          / Delivery Challan / Proforma Invoice
 *
 * The `resolveLogoUrl` helper converts a relative `/api/companies/<cid>/logo`
 * URL into an absolute one anchored on `REACT_APP_BACKEND_URL` — the print
 * window opens under a `blob:` origin and can't resolve relative paths.
 */

/** Absolute API base picked up from the same env used by `axios`. Falls back
 *  to the current window origin when running client-side without the env
 *  (which never happens in the shipped build but keeps SSR-style guards
 *  simple). */
const API_BASE =
  (typeof process !== "undefined" && process.env && process.env.REACT_APP_BACKEND_URL) ||
  (typeof window !== "undefined" ? window.location.origin : "");

export function resolveLogoUrl(url) {
  if (!url) return "";
  if (/^https?:\/\//i.test(url) || /^data:/i.test(url)) return url;
  if (url.startsWith("/")) return `${API_BASE}${url}`;
  return url;
}

export const TEMPLATE_OVERRIDES = {
  modern: "", // default — no overrides
  classic: `
    /* Classic — print-shop aesthetic: serif type, single-rule borders,
       monotone accent, no shaded boxes. Compliance content unchanged. */
    body { font-family: 'Times New Roman', 'IBM Plex Serif', serif; color: #111; }
    .header { border-bottom: 1px solid #111 !important; }
    .header .supplier .gstin { color: #111 !important; font-family: 'Courier New', monospace; }
    .header .meta .title { color: #111 !important; text-transform: uppercase; letter-spacing: 0.08em; font-size: 15px; }
    .party-box, .totals, .bank, .sign, .words, .terms, .ref, .pos-bar {
      background: #fff !important;
      border: 1px solid #111 !important;
      border-radius: 0 !important;
    }
    .party-box .gstin { color: #111 !important; }
    table.lines th { background: #111 !important; }
    table.lines td, table.lines th { border-color: #111 !important; }
    table.hsn-summary th { background: #eee !important; color: #111 !important; }
    table.hsn-summary td, table.hsn-summary th { border-color: #111 !important; }
    .totals .grand { color: #111 !important; }
  `,
  compact: `
    /* Compact — tight spacing, smaller header, no ship-block gutter. Prints
       most single-page invoices in ~30% less vertical space. */
    body { padding: 14px 20px !important; font-size: 10.5px !important; line-height: 1.35 !important; }
    .header { padding-bottom: 8px !important; }
    .header .supplier h1 { font-size: 16px !important; }
    .header .meta .title { font-size: 14px !important; }
    .parties { grid-template-columns: 1fr 1fr; gap: 8px !important; margin-top: 8px !important; }
    .party-box { padding: 6px 8px !important; }
    table.lines { margin-top: 8px !important; font-size: 9.5px !important; }
    table.lines th, table.lines td { padding: 4px 6px !important; }
    .totals-grid, .footer { gap: 8px !important; margin-top: 8px !important; }
    .totals { padding: 6px 8px !important; }
    .totals .grand { font-size: 12.5px !important; margin-top: 4px !important; padding-top: 4px !important; }
    .terms { margin-top: 8px !important; padding: 6px 8px !important; font-size: 9px !important; }
    .footnote, .disclosure, .non-tax-banner { margin-top: 6px !important; }
  `,
};

export function templateCss(template) {
  const key = String(template || "modern").toLowerCase();
  return TEMPLATE_OVERRIDES[key] || "";
}
